// bd-deepcontext-hfv7u.7 — pure formatters for the summary KPI strip. Kept
// separate from JobRunsModal so the value formatting (match-rate %, durations,
// empty/null guards) is unit-testable without React.

// Per-job aggregate returned by GET /api/scheduled-jobs/:id/runs/aggregate
// (epic deepcontext-hfv7u). Mirrors the backend's ScheduledJobRunsAggregate so
// the KPI strip is correct beyond the fetched window, not a client-side reduce.
export interface ScheduledJobRunsAggregate {
  jobId: string;
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  // Raw SUM of each run's matchedUrls across all runs (no de-dup, no status
  // filter). A URL matched in two runs counts twice; intentional.
  cumulativeMatchedUrls: number;
  // Mean (matchedUrls / totalUrls) across completed runs, 0..1; null when
  // there are no completed runs.
  avgMatchRate: number | null;
  // Mean (completedAt - startedAt) in ms over completed runs; null when none.
  avgDurationMs: number | null;
}

const EM_DASH = "—";

/** Format avgMatchRate (0..1, or null) as a percentage string, e.g. "73%". */
export function formatMatchRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) {
    return EM_DASH;
  }
  const clamped = Math.max(0, Math.min(1, rate));
  return `${Math.round(clamped * 100)}%`;
}

/**
 * Format a duration in ms as a compact human string: "1.2s", "3m 5s", "2h 4m".
 * null/non-finite → em dash.
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) {
    return EM_DASH;
  }
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    // Sub-minute: one decimal for legibility (e.g. "1.2s"), whole for <1s edge.
    return `${totalSeconds < 10 ? totalSeconds.toFixed(1) : Math.round(totalSeconds)}s`;
  }
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) {
    const seconds = Math.round(totalSeconds - totalMinutes * 60);
    return seconds > 0 ? `${totalMinutes}m ${seconds}s` : `${totalMinutes}m`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

export interface KpiCell {
  label: string;
  value: string;
}

/**
 * Build the ordered KPI cells from the aggregate. A null aggregate (not loaded
 * yet / failed) renders every value as an em dash so the strip's shape is
 * stable. Counts always render (0 is a real value); rate/duration guard null.
 *
 * Five cells (per the agreed mockup): Avg match rate · Total matched · Total
 * runs · Completed (with failed count merged in, e.g. "59 / 4 failed") · Avg
 * duration.
 */
export function buildKpiCells(
  aggregate: ScheduledJobRunsAggregate | null,
): KpiCell[] {
  return [
    {
      label: "Avg match rate",
      value: aggregate ? formatMatchRate(aggregate.avgMatchRate) : EM_DASH,
    },
    {
      label: "Total matched",
      value: aggregate
        ? aggregate.cumulativeMatchedUrls.toLocaleString("en-US")
        : EM_DASH,
    },
    {
      label: "Total runs",
      value: aggregate ? String(aggregate.totalRuns) : EM_DASH,
    },
    {
      label: "Completed",
      value: aggregate
        ? `${aggregate.completedRuns} / ${aggregate.failedRuns} failed`
        : EM_DASH,
    },
    {
      label: "Avg duration",
      value: aggregate ? formatDurationMs(aggregate.avgDurationMs) : EM_DASH,
    },
  ];
}
